<template>
  <div>
    <div class="section text-center pb-0">
      <span class="ws-eyebrow">Veelgestelde vragen</span>
      <h1 class="ws-h1 mt-3 mx-auto faq-title">{{ page.title }}</h1>
      <p class="ws-lead mx-auto faq-intro">{{ page.intro }}</p>
      <p class="faq-updated">Bijgewerkt op 5 oktober 2026</p>

      <nav class="faq-switch" aria-label="Veelgestelde vragen per doelgroep">
        <router-link
          v-for="link in faqLinks"
          :key="link.link"
          :to="link.link"
          class="faq-switch-item"
          :class="{ active: $route.path === link.link }"
          :aria-current="$route.path === link.link ? 'page' : undefined"
          >{{ link.text }}</router-link
        >
      </nav>
    </div>

    <div class="section faq-body" :class="{ 'faq-body--topics': hasTopics }">
      <aside v-if="hasTopics" class="faq-topics" aria-label="Onderwerpen">
        <span class="faq-topics-label">Onderwerpen</span>
        <div class="faq-topics-list">
          <button
            v-for="group in page.groups"
            :key="group.id"
            class="faq-topic"
            @click="scrollTo(group.id)"
          >
            {{ group.title }}
          </button>
        </div>
      </aside>

      <div class="faq-groups" @click="onContentClick">
        <section
          v-for="group in page.groups"
          :key="group.id"
          :id="group.id"
          class="faq-group"
        >
          <h2 v-if="group.title" class="faq-group-title">{{ group.title }}</h2>
          <div class="faq-list">
            <div
              v-for="(item, i) in group.items"
              :key="i"
              class="faq-item"
              :class="{ open: isOpen(group.id, i) }"
            >
              <h3 class="faq-q-heading">
                <button
                  class="faq-q"
                  :id="`${group.id}-${i}-vraag`"
                  :aria-expanded="isOpen(group.id, i) ? 'true' : 'false'"
                  :aria-controls="`${group.id}-${i}-antwoord`"
                  @click="toggle(group.id, i)"
                >
                  <span>{{ item.q }}</span>
                  <span class="faq-icon" aria-hidden="true"></span>
                </button>
              </h3>
              <div
                class="faq-a"
                :id="`${group.id}-${i}-antwoord`"
                role="region"
                :aria-labelledby="`${group.id}-${i}-vraag`"
              >
                <div class="faq-a-clip">
                  <div class="faq-a-body ws-prose" v-html="item.a"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div class="section pt-0 faq-help-wrap">
      <div class="ws-card faq-help ws-reveal" v-reveal>
        <div>
          <h2 class="faq-help-title">Staat uw vraag er niet tussen?</h2>
          <p class="ws-lead mb-0">
            Neem gerust contact met ons op via
            <a href="mailto:info@weshare4kids.com" class="faq-help-link"
              >info@weshare4kids.com</a
            >
            of bel
            <a href="tel:+31854828109" class="faq-help-link">085 4828109</a>.
          </p>
        </div>
        <router-link to="/contact" class="ws-btn ws-btn--primary"
          >Naar contact</router-link
        >
      </div>
    </div>
  </div>
</template>

<script>
import { useMeta } from "vue-meta";
import { faqPages, faqLinks } from "@/data/faq";

export default {
  props: {
    doelgroep: { type: String, required: true },
  },
  setup(props) {
    useMeta({
      title: `Veelgestelde vragen ${faqPages[
        props.doelgroep
      ].title.toLowerCase()}`,
    });
  },
  data() {
    return {
      faqLinks,
      open: {},
    };
  },
  computed: {
    page() {
      return faqPages[this.doelgroep];
    },
    hasTopics() {
      return this.page.groups.length > 1;
    },
  },
  methods: {
    isOpen(groupId, i) {
      return !!this.open[`${groupId}-${i}`];
    },
    toggle(groupId, i) {
      const key = `${groupId}-${i}`;
      this.open[key] = !this.open[key];
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (!el) return;
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      el.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "start",
      });
    },
    // Links in de antwoorden zijn gewone <a>-tags (v-html); interne links
    // gaan via de router zodat de pagina niet volledig herlaadt.
    onContentClick(event) {
      const link = event.target.closest("a");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href.startsWith("#")) {
        event.preventDefault();
        this.scrollTo(href.slice(1));
      } else if (href.startsWith("/")) {
        event.preventDefault();
        this.$router.push(href);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.faq-title {
  max-width: 760px;
}
.faq-intro {
  max-width: 680px;
  margin-top: 16px;
}
.faq-updated {
  font-size: 13.5px;
  color: #8a7c6f;
  margin-top: 12px;
}

/* ---- Doelgroep-schakelaar ---- */
.faq-switch {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  margin-top: 30px;
  padding: 5px;
  background: #fff;
  border: 1px solid rgba(42, 32, 24, 0.06);
  border-radius: 16px;
  box-shadow: 0 10px 30px -20px rgba(60, 36, 21, 0.3);
}
.faq-switch-item {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 15px;
  color: #6b5d52;
  text-decoration: none;
  padding: 10px 18px;
  border-radius: 11px;
  transition: background var(--ws-dur-fast) var(--ws-ease-out),
    color var(--ws-dur-fast) var(--ws-ease-out);

  &:hover {
    color: #2a2018;
    background: rgba(42, 32, 24, 0.04);
  }
  &.active {
    color: #fff;
    background: #ea6b26;
  }
}

/* ---- Indeling: onderwerpen links, vragen rechts ---- */
.faq-body {
  max-width: 860px;
  padding-top: 56px;
}
.faq-body--topics {
  max-width: 1200px;
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 56px;
  align-items: start;
}

.faq-topics {
  position: sticky;
  top: 100px;
}
.faq-topics-label {
  display: block;
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c2541a;
  margin-bottom: 10px;
}
.faq-topics-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.faq-topic {
  text-align: left;
  font-family: "Nunito", sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #5c4f44;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 10px;
  transition: background var(--ws-dur-fast) var(--ws-ease-out),
    color var(--ws-dur-fast) var(--ws-ease-out);

  &:hover {
    color: #2a2018;
    background: rgba(234, 107, 38, 0.1);
  }
}

.faq-group {
  scroll-margin-top: 100px;
}
.faq-group + .faq-group {
  margin-top: 44px;
}
.faq-group-title {
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 24px;
  color: #241b12;
  margin-bottom: 14px;
}

/* ---- Uitklapbare vraag ---- */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.faq-item {
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(42, 32, 24, 0.06);
  box-shadow: 0 10px 30px -22px rgba(60, 36, 21, 0.3);
  transition: border-color var(--ws-dur-fast) var(--ws-ease-out);

  &.open {
    border-color: rgba(234, 107, 38, 0.35);
  }
}
.faq-q-heading {
  margin: 0;
}
.faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  text-align: left;
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 17px;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: #241b12;
  background: none;
  border: none;
  cursor: pointer;
  padding: 20px 22px 20px 24px;
  border-radius: 18px;

  &:hover {
    color: #c2541a;
  }
}

/* Plus dat bij openen een min wordt */
.faq-icon {
  position: relative;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fbe3d2;
  transition: background var(--ws-dur-fast) var(--ws-ease-out);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 2px;
    margin: -1px 0 0 -6px;
    border-radius: 2px;
    background: #c2541a;
    transition: transform var(--ws-dur-base) var(--ws-ease-out),
      background var(--ws-dur-fast) var(--ws-ease-out);
  }
  &::after {
    transform: rotate(90deg);
  }
}
.open .faq-icon {
  background: #ea6b26;

  &::before,
  &::after {
    background: #fff;
  }
  &::after {
    transform: rotate(0deg);
  }
}

/* Hoogte animeren via grid-rows; dicht = onzichtbaar voor schermlezers */
.faq-a {
  display: grid;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition: grid-template-rows var(--ws-dur-base) var(--ws-ease-out),
    visibility 0s linear var(--ws-dur-base);
}
.open .faq-a {
  grid-template-rows: 1fr;
  visibility: visible;
  transition: grid-template-rows var(--ws-dur-base) var(--ws-ease-out),
    visibility 0s;
}
.faq-a-clip {
  overflow: hidden;
}
.faq-a-body {
  font-size: 16px;
  padding: 0 24px 22px;

  :deep(p:last-child),
  :deep(ul:last-child),
  :deep(ol:last-child) {
    margin-bottom: 0;
  }
}

/* ---- Hulpblok onderaan ---- */
.faq-help-wrap {
  max-width: 860px;
}
.faq-help {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 32px 36px;
}
.faq-help-title {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 22px;
  color: #241b12;
  margin-bottom: 6px;
}
.faq-help-link {
  color: #c2541a;
  font-weight: 700;
  white-space: nowrap;
}

@media screen and (max-width: 900px) {
  .faq-body--topics {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .faq-topics {
    position: static;
  }
  .faq-topics-list {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
  .faq-topic {
    font-size: 14px;
    padding: 7px 13px;
    background: #fff;
    border: 1px solid rgba(42, 32, 24, 0.08);
  }
  .faq-help {
    flex-direction: column;
    align-items: flex-start;
    padding: 28px 24px;
  }
}

@media screen and (max-width: 600px) {
  .faq-switch {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  .faq-q {
    font-size: 16px;
    padding: 18px 16px 18px 18px;
  }
  .faq-a-body {
    padding: 0 18px 20px;
  }
}
</style>
